import { useCallback, useState } from 'react';
import type {
  OverlayEntry,
  ScreenEntry,
  ScreenId,
  ScreenParams,
} from './screenIds';

export type ScreenNavigation = {
  stack: ScreenEntry[];
  current: ScreenEntry;
  overlay: OverlayEntry | null;
  push: (id: ScreenId, params?: ScreenParams) => void;
  replace: (id: ScreenId, params?: ScreenParams) => void;
  replaceRoot: (id: ScreenId, params?: ScreenParams) => void;
  goBack: () => void;
  openOverlay: (id: OverlayEntry['id']) => void;
  closeOverlay: () => void;
};

export function useScreenNavigation(
  initial: ScreenEntry = { id: 'loading' },
): ScreenNavigation {
  const [stack, setStack] = useState<ScreenEntry[]>([initial]);
  const [overlay, setOverlay] = useState<OverlayEntry | null>(null);

  const current = stack[stack.length - 1] ?? initial;

  const push = useCallback((id: ScreenId, params?: ScreenParams) => {
    setStack((prev) => [...prev, { id, params }]);
  }, []);

  const replace = useCallback((id: ScreenId, params?: ScreenParams) => {
    setStack((prev) => {
      if (prev.length === 0) {
        return [{ id, params }];
      }
      return [...prev.slice(0, -1), { id, params }];
    });
  }, []);

  const replaceRoot = useCallback((id: ScreenId, params?: ScreenParams) => {
    setOverlay(null);
    setStack([{ id, params }]);
  }, []);

  const goBack = useCallback(() => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const openOverlay = useCallback((id: OverlayEntry['id']) => {
    setOverlay({ id });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlay(null);
  }, []);

  return {
    stack,
    current,
    overlay,
    push,
    replace,
    replaceRoot,
    goBack,
    openOverlay,
    closeOverlay,
  };
}
